/* ==============================================================================
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
============================================================================== */

import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Swal from 'sweetalert2';

// @mui material components
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Icon from '@mui/material/Icon';
import InputAdornment from '@mui/material/InputAdornment';
import Tooltip from '@mui/material/Tooltip';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import TableHead from '@mui/material/TableHead';
import IconButton from '@mui/material/IconButton';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDInput from 'components/MDInput';
import MDButton from 'components/MDButton';

// Material Dashboard 2 PRO React examples
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';
import PosCartList from './PosCartList';

const initAttr = {
  id: '',
};

function DbCartListDelete() {
  const mod = 'db_pos_listdel';
  const { t, i18n } = useTranslation();
  const app = useSelector((state) => state[APP_STATE]);
  const [record, setRecord] = useState([]);
  const [notify, setNotify] = useState(null);
  const [loading, setLoading] = useState(false);
  const [attr, setAttr] = useState(initAttr);
  const txtSearch = useRef(null);

  const showNotify = (msg, color) => {
    setNotify(
      <Snackbar
        open
        autoHideDuration={3000}
        onClose={() => setNotify(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setNotify(null)} severity={color} variant='filled'>
          {msg}
        </Alert>
      </Snackbar>
    );
  };

  const view = () => {
    FetchData('POST', `/${mod}`, app.token, {
      lang: app.lang,
      search: txtSearch.current.value,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);
      } else {
        Func.exitApp();
      }
    });
  };

  const edit = (item) => {
    Swal.fire({
      title: t(`alert.confirm_edit_title`),
      icon: 'warning',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: t(`element.save`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);

        FetchData('PATCH', `/${mod}/${item.id}`, app.token, {
          enable: 'Y',
        }).then((result) => {
          setLoading(false);

          if (result.status === 'ok') {
            view();
            showNotify(t(`alert.editcomplete`), 'success');
          } else {
            showNotify(result.msg, 'error');
          }
        });
      }
    });
  };

  const del = (item) => {
    Swal.fire({
      title: t(`alert.confirm_delete_title`),
      icon: 'warning',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: t(`element.deleteconfirm`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);

        FetchData('DELETE', `/${mod}/${item.id}`, app.token, {}).then((result) => {
          setLoading(false);

          if (result.status === 'ok') {
            view();
            showNotify(t(`alert.deletecomplete`), 'success');
          } else {
            showNotify(t(`alert.deleteerror`), 'error');
          }
        });
      }
    });
  };

  const btnDel = (item) => {
    return (
      <Tooltip title='ลบ'>
        <IconButton aria-label='delete' color='error' size='small' onClick={() => del(item)}>
          <Icon>delete</Icon>
        </IconButton>
      </Tooltip>
    );
  };

  const btnCancel = (item) => {
    return (
      <Tooltip title='ยกเลิก'>
        <IconButton aria-label='cancel' color='warning' size='small' onClick={() => edit(item)}>
          <Icon>close</Icon>
        </IconButton>
      </Tooltip>
    );
  };

  const btnOpen = (item) => {
    return (
      <MDButton
        variant='text'
        color='info'
        size='small'
        sx={{ fontSize: '1rem', whiteSpace: 'nowrap' }}
        onClick={() => {
          setAttr({ id: item.cart_id });
        }}
      >
        ดูตะกร้า
      </MDButton>
    );
  };

  const renderSearch = () => {
    return (
      <MDBox
        noValidate
        component='form'
        role='form'
        onSubmit={(e) => {
          e.preventDefault();
          view();
        }}
      >
        <MDInput
          name='searchform'
          fullWidth
          type='search'
          InputProps={{
            inputRef: txtSearch,
            startAdornment: (
              <InputAdornment position='start'>{t('element.search')} :</InputAdornment>
            ),
            autoComplete: 'off',
          }}
        />
      </MDBox>
    );
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: 9999 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const renderOrder = () => {
    return (
      <MDBox sx={{ minHeight: 500 }}>
        <MDBox mb={2}>{renderSearch()}</MDBox>
        <TableContainer sx={{ boxShadow: 0 }}>
          <Table aria-label='simple table'>
            <TableHead>
              <TableRow>
                <TableCell align='center' width={20}>
                  No.
                </TableCell>
                <TableCell align='center' width={40}>
                  รูป
                </TableCell>
                <TableCell>สินค้า</TableCell>
                <TableCell align='center'>รอบ</TableCell>
                <TableCell align='center'>สาขา</TableCell>
                <TableCell align='center'>POS</TableCell>
                <TableCell align='right' width={90}>
                  ราคา
                </TableCell>
                <TableCell align='right' width={90}>
                  จำนวน
                </TableCell>
                <TableCell align='left' width={50}>
                  หน่วย
                </TableCell>
                <TableCell align='right' width={90}>
                  รวม
                </TableCell>
                <TableCell width={40}>&nbsp;</TableCell>
                <TableCell width={40}>&nbsp;</TableCell>
                <TableCell width={40}>&nbsp;</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {record.map((row, i) => (
                <TableRow className='row-order' key={row.id}>
                  <TableCell align='center'>{i + 1}</TableCell>
                  <TableCell>{Func.imageDisplay(row)}</TableCell>
                  <TableCell>
                    <MDBox className='nowrap'>
                      {row.code} <span className='color-default'>{row.sku}</span>
                    </MDBox>
                    <MDBox className='cut' width={200}>
                      {row.name}
                    </MDBox>
                  </TableCell>
                  <TableCell align='center'>{row.shift_code}</TableCell>
                  <TableCell align='center'>{row.branch_code}</TableCell>
                  <TableCell align='center'>{row.cashier_name}</TableCell>
                  <TableCell align='right'>{Func.numberDisplay(row.price)}</TableCell>
                  <TableCell align='right'>{Func.numberDisplay(row.quantity)}</TableCell>
                  <TableCell align='left' className='nowrap'>
                    {row.unit_name}
                  </TableCell>
                  <TableCell align='right'>{Func.numberDisplay(row.total)}</TableCell>
                  <TableCell align='center'>{btnDel(row)}</TableCell>
                  <TableCell align='center'>{btnCancel(row)}</TableCell>
                  <TableCell align='center'>{btnOpen(row)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </MDBox>
    );
  };

  const renderOpen = () => {
    return (
      <MDBox sx={{ minHeight: 500 }}>
        <MDBox mb={2}>
          <MDButton
            variant='contained'
            color='dark'
            onClick={() => {
              setAttr(initAttr);
            }}
          >
            <Icon>arrow_back</Icon>&nbsp;{t(`element.back`)}
          </MDButton>
        </MDBox>
        <PosCartList parent={attr} type='delete' />
      </MDBox>
    );
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    view();
  }, []);

  return (
    <MDBox>
      {attr.id === '' ? renderOrder() : renderOpen()}
      {renderLoading()}
      {notify}
    </MDBox>
  );
}

export default DbCartListDelete;
